/**
 * Sprachvisualisierung — Koerper und Animation getrennt.
 *
 * Der Koerper ist die Kugel, die Animation liegt darueber; beides laesst sich
 * frei kombinieren. Alle Farben kommen aus den Theme-Tokens (`--primary`,
 * `--foreground`, `--background`, `--card`), damit die Figur in jedem der
 * Themes aus `themes.ts` steht — auch in den farblosen wie `janeway`, wo die
 * Palette ueber Helligkeit statt ueber Farbton gestaffelt wird.
 *
 * Bewusst ohne React und ohne DOM-Abhaengigkeit: nur ein Canvas-Kontext, eine
 * Konfiguration und ein Zustand. Die Einbindung macht `VoiceOrbCanvas`.
 */

export type VoiceOrbBody = "nebula" | "glow" | "veinsSolid" | "veins" | "iris" | "glass" | "hollow";
export type VoiceOrbEffect = "breath" | "wobble" | "aura" | "orbit" | "stream" | "rings" | "sparks";

export interface VoiceOrbConfig {
  body: VoiceOrbBody;
  /**
   * 0…1 — Groesse des KOERPERS: sein Durchmesser laeuft von 34 % auf 68 % der
   * kurzen Buehnenseite (`R = min(w,h)*(0.17+0.17*size)`).
   *
   * Nicht die Ausdehnung der ganzen Figur: der Schein reicht bis 2,9*R und
   * wird, wo die Buehne enger ist, auf den verfuegbaren Platz gebracht. In
   * einer knappen Box aendert der Regler deshalb sichtbar den Ball, waehrend
   * der Hof weiter die Box fuellt. Hier stand bis 0.232.0 „Anteil der kurzen
   * Buehnenseite, den die Figur einnimmt" — das las sich wie eine Zusage ueber
   * die Gesamtausdehnung, die der Code nie gegeben hat.
   */
  size: number;
  /** 1…4 Bahnen; gilt fuer „Baender" und „Stromring" gemeinsam. */
  bands: number;
  fx: VoiceOrbEffect[];
  /** Anruf-Figur (ai-chat): welche der fuenf Darstellungen. Fehlt = Standard. */
  figur?: FigurVariante;
  /** Anruf-Figur: wie viele Toene aus dem Theme-Akzent entstehen. Fehlt = Standard. */
  farben?: FigurFarben;
  /** Anruf-Ansicht: Licht fuer Pille, Vollbild und Begleiter. Fehlt = aus `figur` hergeleitet. */
  licht?: AnrufLicht;
  ansicht?: AnrufAnsicht;
  figurArt?: AnrufFigurArt;
  /** Schwarm (Kugel aus Lichtpunkten) zusaetzlich zum Licht. */
  schwarm?: boolean;
  vollbildText?: AnrufVollbildText;
}

export type AnrufLicht = "kein" | "licht" | "punkte";
export type AnrufAnsicht = "normal" | "vollbild";
export type AnrufFigurArt = "normal" | "begleiter";
export type AnrufVollbildText = "satz" | "badges";

export const ANRUF_LICHT: { id: AnrufLicht; label: string }[] = [
  { id: "kein", label: "Kein Licht" },
  { id: "licht", label: "Licht" },
  { id: "punkte", label: "Licht mit Punkten" },
];
export const ANRUF_ANSICHT: { id: AnrufAnsicht; label: string }[] = [
  { id: "normal", label: "Normal" },
  { id: "vollbild", label: "Vollbild" },
];
export const ANRUF_FIGUR_ART: { id: AnrufFigurArt; label: string }[] = [
  { id: "normal", label: "Normal" },
  { id: "begleiter", label: "Begleiter" },
];
export const ANRUF_VOLLBILD_TEXT: { id: AnrufVollbildText; label: string }[] = [
  { id: "satz", label: "Ein Satz" },
  { id: "badges", label: "Nur Status und Badges" },
];

const idsVon = (k: { id: string }[]): Set<string> => new Set(k.map((e) => e.id));
const LICHT_IDS = idsVon(ANRUF_LICHT);
const ANSICHT_IDS = idsVon(ANRUF_ANSICHT);
const FIGUR_ART_IDS = idsVon(ANRUF_FIGUR_ART);
const VOLLBILD_TEXT_IDS = idsVon(ANRUF_VOLLBILD_TEXT);

export type FigurVariante = "horizont" | "punkte" | "schwarm" | "kombi" | "punktekombi";
export type FigurFarben = "ein" | "zwei" | "vier";

export const FIGUR_VARIANTEN: { id: FigurVariante; label: string; hint: string }[] = [
  { id: "horizont", label: "Horizont", hint: "Sechs weiche Lichtkörper vom unteren Rand" },
  { id: "punkte", label: "Punkte", hint: "Horizont, in Lichtpunkten aufgelöst" },
  { id: "schwarm", label: "Schwarm", hint: "Eine Kugel aus Lichtpunkten" },
  { id: "kombi", label: "Horizont + Schwarm", hint: "Lichtkörper und Kugel zusammen" },
  { id: "punktekombi", label: "Punkte + Schwarm", hint: "Lichtpunkte und Kugel zusammen" },
];

export const FIGUR_FARBEN: { id: FigurFarben; label: string; hint: string }[] = [
  { id: "ein", label: "Einfarbig", hint: "Nur der Akzent des Themes" },
  { id: "zwei", label: "Zweifarbig", hint: "Akzent und ein Nachbarton" },
  { id: "vier", label: "Vierfarbig", hint: "Vier Töne aus dem Akzent" },
];

export const FIGUR_STANDARD: { figur: FigurVariante; farben: FigurFarben } = {
  figur: "horizont",
  farben: "vier",
};

const FIGUR_IDS = new Set<string>(FIGUR_VARIANTEN.map((v) => v.id));
const FARBEN_IDS = new Set<string>(FIGUR_FARBEN.map((v) => v.id));

/** Figur und Farben einer Einstellung, mit Standard fuer alles Fehlende. */
export function figurAusConfig(cfg: VoiceOrbConfig | null | undefined): {
  figur: FigurVariante;
  farben: FigurFarben;
} {
  return {
    figur: cfg?.figur ?? FIGUR_STANDARD.figur,
    farben: cfg?.farben ?? FIGUR_STANDARD.farben,
  };
}

const AUS_FIGUR: Record<FigurVariante, { licht: AnrufLicht; schwarm: boolean }> = {
  horizont: { licht: "licht", schwarm: false },
  punkte: { licht: "punkte", schwarm: false },
  schwarm: { licht: "kein", schwarm: true },
  kombi: { licht: "licht", schwarm: true },
  punktekombi: { licht: "punkte", schwarm: true },
};

export interface AnrufAnsichtWerte {
  licht: AnrufLicht;
  farben: FigurFarben;
  ansicht: AnrufAnsicht;
  figurArt: AnrufFigurArt;
  schwarm: boolean;
  vollbildText: AnrufVollbildText;
}

/**
 * Vollstaendige Anruf-Ansicht einer Einstellung. Fehlen die neuen Felder, gilt das alte `figur`.
 * „Kein Licht" ohne Schwarm zeigte nichts: dann bleibt der Schwarm an. Der Begleiter greift nur in Labs
 * (in Prod ist er ausgegraut, auch ein gespeicherter Wert gilt dort nicht).
 */
export function anrufAnsichtAusConfig(
  cfg: VoiceOrbConfig | null | undefined,
  { labs }: { labs: boolean },
): AnrufAnsichtWerte {
  const alt = AUS_FIGUR[cfg?.figur ?? FIGUR_STANDARD.figur];
  const licht = cfg?.licht ?? alt.licht;
  const schwarm = licht === "kein" ? true : (cfg?.schwarm ?? alt.schwarm);
  return {
    licht,
    farben: cfg?.farben ?? FIGUR_STANDARD.farben,
    ansicht: cfg?.ansicht ?? "normal",
    figurArt: labs ? (cfg?.figurArt ?? "normal") : "normal",
    schwarm,
    vollbildText: cfg?.vollbildText ?? "satz",
  };
}

/** Was die Figur gerade tut. Vier Groessen statt einer — sonst unterscheiden
 *  sich Zuhoeren, Denken und Sprechen nur im Ausschlag und nicht im Bild. */
export interface VoiceOrbState {
  /** Momentaner Ausschlag 0…1,4. */
  level: number;
  /** Dreh- und Wandergeschwindigkeit. */
  spin: number;
  /** 0 weit und locker, 1 eng gebuendelt. */
  focus: number;
  /** Gewicht der Silbenstoesse. */
  pulse: number;
  /** Langsames Ein- und Ausatmen der ganzen Figur. */
  breath: number;
  /** Zeitstempel des letzten Silbenstosses (Sekunden, gleiche Uhr wie `t`). */
  lastSyllable: number;
}

export const VOICE_ORB_BODIES: { id: VoiceOrbBody; label: string; hint: string }[] = [
  { id: "nebula", label: "Nebel", hint: "Die bisherige Darstellung aus dem Testanruf" },
  { id: "glow", label: "Beleuchtet", hint: "Licht, Schattengrenze, Randlicht, Glanzpunkt" },
  { id: "veinsSolid", label: "Adern, voll", hint: "Beleuchtete Kugel, Adern nur auf der Vorderseite" },
  { id: "veins", label: "Adern, durchsichtig", hint: "Bahnen rundum sichtbar, kein deckender Körper" },
  { id: "iris", label: "Irisierend", hint: "Farbflecken unter der Haut, Saum am Rand" },
  { id: "glass", label: "Glashülle", hint: "Durchsichtige Kugel, Wellenband darin" },
  { id: "hollow", label: "Lichtring", hint: "Kein Körper — verdrehte Bänder, der Kern bleibt leer" },
];

export const VOICE_ORB_EFFECTS: { id: VoiceOrbEffect; label: string; hint: string }[] = [
  { id: "breath", label: "Atmen", hint: "Größe geht mit Atem und Pegel" },
  { id: "wobble", label: "Verformen", hint: "Silhouette beult beim Sprechen aus" },
  { id: "aura", label: "Aura", hint: "Schein wächst mit dem Pegel" },
  { id: "orbit", label: "Bänder", hint: "Schleier umlaufen die Kugel" },
  { id: "stream", label: "Stromring", hint: "Teilchen strömen auf geneigten Bahnen" },
  { id: "rings", label: "Ringe je Silbe", hint: "Jeder Stoß schickt eine Welle nach außen" },
  { id: "sparks", label: "Funken", hint: "Silben werfen Punkte nach außen" },
];

/**
 * Vorgabe des Betreibers (2026-08-18), woertlich: „adern voll ; atmen + aura +
 * baender + stromring + funken (3 ringe)". Gegen die Kataloge oben gelesen:
 * „Adern, voll" = `veinsSolid`, „Atmen" = `breath`, „Aura" = `aura`,
 * „Baender" = `orbit`, „Stromring" = `stream`, „Funken" = `sparks`.
 *
 * „(3 ringe)" ist `bands: 3`, NICHT der Effekt `rings`: der heisst im UI
 * „Ringe je Silbe" und traegt keine Zahl, waehrend `bands` (Regler „Bahnen",
 * 1-4) genau die Baender und den Stromring zaehlt — beide hat er an. `rings`
 * faellt damit aus der Vorgabe heraus; bis hierher war es drin.
 *
 * Sie gilt fuer alle, die NICHTS gespeichert haben — also auch rueckwirkend
 * fuer bisherige Nutzer ohne eigene Wahl, nicht nur fuer neue.
 */
export const VOICE_ORB_DEFAULT: VoiceOrbConfig = {
  body: "veinsSolid",
  size: 0.42,
  bands: 3,
  fx: ["breath", "aura", "orbit", "stream", "sparks"],
};

const TAU = Math.PI * 2;
const clamp = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;
const smoothstep = (e0: number, e1: number, x: number): number => {
  const t = clamp((x - e0) / (e1 - e0), 0, 1);
  return t * t * (3 - 2 * t);
};

interface Hsl {
  h: number;
  s: number;
  l: number;
}

/* ── Rauschen ─────────────────────────────────────────────────────────────── */
const PERM = new Uint8Array(512);
{
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  let seed = 20260818;
  const rnd = (): number => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 4294967296;
  };
  for (let i = 255; i > 0; i--) {
    const j = (rnd() * (i + 1)) | 0;
    const tmp = p[i];
    p[i] = p[j];
    p[j] = tmp;
  }
  for (let i = 0; i < 512; i++) PERM[i] = p[i & 255];
}
const fade = (t: number): number => t * t * t * (t * (t * 6 - 15) + 10);
function h3(i: number, j: number, k: number): number {
  return PERM[(PERM[(PERM[i & 255] + j) & 255] + k) & 255] / 127.5 - 1;
}
function n3(x: number, y: number, z: number): number {
  const xi = Math.floor(x), yi = Math.floor(y), zi = Math.floor(z);
  const fx = fade(x - xi), fy = fade(y - yi), fz = fade(z - zi);
  const x00 = lerp(h3(xi, yi, zi), h3(xi + 1, yi, zi), fx);
  const x10 = lerp(h3(xi, yi + 1, zi), h3(xi + 1, yi + 1, zi), fx);
  const x01 = lerp(h3(xi, yi, zi + 1), h3(xi + 1, yi, zi + 1), fx);
  const x11 = lerp(h3(xi, yi + 1, zi + 1), h3(xi + 1, yi + 1, zi + 1), fx);
  return lerp(lerp(x00, x10, fy), lerp(x01, x11, fy), fz);
}
/**
 * Rauschen ueber einen Winkel MUSS periodisch sein: sonst klafft eine
 * geschlossene Kurve bei 0 auseinander und `closePath()` zieht eine Gerade
 * darueber — sichtbar als rechteckiger Abriss. Deshalb wird entlang eines
 * Kreises abgetastet, nicht entlang einer Geraden.
 */
function angleNoise(a: number, f: number, t: number): number {
  return n3(Math.cos(a) * f + 13, Math.sin(a) * f + 7, t);
}

/* ── Farbe ────────────────────────────────────────────────────────────────── */
function rgbNachHsl(r: number, g: number, b: number): Hsl {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d) h = mx === r ? (g - b) / d + (g < b ? 6 : 0) : mx === g ? (b - r) / d + 2 : (r - g) / d + 4;
  const l = (mx + mn) / 2;
  return { h: h * 60, s: d ? (d / (1 - Math.abs(2 * l - 1))) * 100 : 0, l: l * 100 };
}

const gemeldeteFarben = new Set<string>();

/**
 * Farbe aus einem Theme-Token. Drei Formate, weil die beiden Aufrufer
 * VERSCHIEDENE liefern — und das war ein echter Fehler, kein Vorrat:
 * `VoiceOrbCanvas` reicht den rohen `hsl()`-String der CSS-Variablen durch,
 * `VoiceVisualizer` laesst den Browser nach `rgba()` rechnen (Zeile 158).
 * Ohne den rgb-Zweig fiel der zweite Weg auf `{0,0,50}` — und weil
 * `mono = prim.s < 12` daraufhin greift, lief die GANZE Palette ueber
 * Helligkeit: die Kugel im Testanruf war in jedem Theme grau. Aufgefallen ist
 * es lange nicht, weil das Standard-Theme `janeway` selbst monochrom ist,
 * dort also grau richtig aussieht.
 */
function parseHsl(str: string): Hsl {
  const m = /hsla?\(\s*([\d.]+)\s*[, ]\s*([\d.]+)%\s*[, ]\s*([\d.]+)%/.exec(str);
  if (m) return { h: +m[1], s: +m[2], l: +m[3] };
  const rgb = /rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/.exec(str);
  if (rgb) return rgbNachHsl(+rgb[1] / 255, +rgb[2] / 255, +rgb[3] / 255);
  const hex = /^#([0-9a-f]{6})$/i.exec(str.trim());
  if (hex) {
    const n = parseInt(hex[1], 16);
    return rgbNachHsl(((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255);
  }
  // Nicht stumm grau werden: ein unbekanntes Format sieht wie ein monochromes
  // Theme aus und ist von einer Gestaltungsentscheidung nicht zu unterscheiden.
  if (typeof console !== "undefined" && !gemeldeteFarben.has(str)) {
    gemeldeteFarben.add(str);
    console.warn(`[voice-orb] Farbformat nicht lesbar, faellt auf Grau zurueck: ${str}`);
  }
  return { h: 0, s: 0, l: 50 };
}
const css = (c: Hsl, a = 1): string =>
  `hsla(${c.h.toFixed(1)},${clamp(c.s, 0, 100).toFixed(1)}%,${clamp(c.l, 0, 100).toFixed(1)}%,${a.toFixed(3)})`;
function shift(c: Hsl, dl: number): Hsl {
  return { h: c.h, s: c.s, l: clamp(c.l + dl, 0, 100) };
}
function mixC(a: Hsl, b: Hsl, t: number): Hsl {
  let dh = b.h - a.h;
  if (dh > 180) dh -= 360;
  else if (dh < -180) dh += 360;
  return { h: (a.h + dh * t + 360) % 360, s: lerp(a.s, b.s, t), l: lerp(a.l, b.l, t) };
}

export interface OrbPalette {
  isDark: boolean;
  bg: Hsl;
  fg: Hsl;
  card: Hsl;
  prim: Hsl;
  /** Praesenteste Farbe: im Dunkeln fast weiss mit Farbstich, im Hellen eine
   *  satte dunkle Tinte. `foreground` waere dort schlicht Schwarz und wirkt
   *  schmutzig. */
  hi: Hsl;
  ink: Hsl[];
  /** Im Dunkeln wird Licht addiert, im Hellen Farbe gemischt. */
  add: GlobalCompositeOperation;
}

/**
 * Palette aus einer Farbfunktion — fuer den Maler im `VoiceVisualizer`, der die
 * Theme-Rollen bereits aufgeloest mitbringt und pro Bild kein
 * `getComputedStyle` vertraegt.
 */
export function paletteFromRoles(farbe: (rolle: string) => string, isDark: boolean): OrbPalette {
  return baueOrbPalette(
    { prim: parseHsl(farbe("primary")), bg: parseHsl(farbe("background")),
      fg: parseHsl(farbe("foreground")), card: parseHsl(farbe("card")) },
    isDark,
  );
}

/** Leitet aus vier Tokens die ganze Palette ab. */
function baueOrbPalette(t: { prim: Hsl; bg: Hsl; fg: Hsl; card: Hsl }, isDark: boolean): OrbPalette {
  const prim = t.prim;
  const mono = prim.s < 12;
  const ink: Hsl[] = [];
  for (let i = 0; i < 5; i++) {
    const f = i / 4;
    if (mono) {
      ink.push({ h: prim.h, s: prim.s, l: clamp(lerp(isDark ? 48 : 26, isDark ? 92 : 64, f), 0, 100) });
    } else {
      ink.push({
        h: (prim.h + (f - 0.5) * 74 + 360) % 360,
        s: clamp(prim.s * (0.72 + 0.5 * Math.abs(f - 0.5)), 22, 100),
        l: clamp(prim.l + (f - 0.5) * (isDark ? 24 : -16), isDark ? 32 : 20, isDark ? 78 : 64),
      });
    }
  }
  return {
    isDark, bg: t.bg, fg: t.fg, card: t.card, prim,
    hi: isDark
      ? { h: prim.h, s: Math.min(prim.s * 0.4, 34), l: 96 }
      : { h: prim.h, s: Math.min(100, prim.s * 1.15), l: mono ? 14 : 24 },
    ink,
    // Im hellen Theme wird NICHT multipliziert: der Aufrufer loescht die Flaeche
    // vor jedem Bild, und multiply gegen Nichts liefert die Quelle — also das
    // weisse Fuellrechteck als sichtbares Viereck.
    add: isDark ? "lighter" : "source-over",
  };
}

/** Liest die Theme-Tokens vom Element und leitet daraus die Palette ab. */
export function readOrbPalette(el: HTMLElement, isDark: boolean): OrbPalette {
  const cs = getComputedStyle(el);
  const tok = (name: string, fallback: string): Hsl =>
    parseHsl(cs.getPropertyValue(name).trim() || fallback);
  return baueOrbPalette(
    { prim: tok("--primary", "hsl(0 0% 98%)"), bg: tok("--background", "hsl(0 0% 0%)"),
      fg: tok("--foreground", "hsl(0 0% 98%)"), card: tok("--card", "hsl(0 0% 5%)") },
    isDark,
  );
}

/* ── Zeichnen ─────────────────────────────────────────────────────────────── */

interface Vein {
  x: number; y: number; z: number;
  hist: number[];
  sp: number; off: number; age: number; span: number; wide: number; ph: number;
}
interface Ring { born: number; power: number }
interface Spark { born: number; a: number; sp: number; size: number }
interface Streamer { u: number; lane: number; sp: number; sz: number }

/**
 * Ein Renderer je Canvas. Haelt die Zwischenpuffer und den Zustand der
 * Teilchen; `render` zeichnet ein Bild.
 */
export class VoiceOrbRenderer {
  private readonly ctx: CanvasRenderingContext2D;
  private readonly buf: HTMLCanvasElement;
  private readonly bctx: CanvasRenderingContext2D;
  private readonly gbuf: HTMLCanvasElement;
  private readonly gctx: CanvasRenderingContext2D;
  private w = 0;
  private h = 0;
  private dpr = 1;
  private bs = 0.5;
  /**
   * Der Ausschnitt, den der Zwischenpuffer abdeckt — der Orb samt seiner
   * weitesten Wirkung, NICHT die ganze Bühne.
   *
   * Bis 0.352.0 war der Puffer immer so gross wie die Fläche, auf die gemalt
   * wird. Der Orb füllt davon aber nur einen Teil: sein Radius ist
   * `min(w,h) * (0.17 + 0.17*size)`, im Testanruf also je nach Bühne ein
   * Viertel bis ein Drittel der kurzen Seite. Zwei `fillRect(0,0,w,h)` in
   * `litBall` und ein Weichzeichner darüber liefen damit über Pixel, die
   * anschliessend die `destination-in`-Maske wieder wegschneidet.
   *
   * Der Ausschnitt ist die Gegenfinanzierung für die volle Geräteauflösung:
   * weniger Fläche, dafür mehr Pixel je Fläche.
   */
  private bx = 0;
  private by = 0;
  private bw = 0;
  private bh = 0;
  private pal!: OrbPalette;
  private veins: Vein[] | null = null;
  private rings: Ring[] = [];
  private sparks: Spark[] = [];
  private streamers: Streamer[] | null = null;
  private lastT: number | null = null;
  private seenSyllRing = -9;
  private seenSyllSpark = -9;

  // Der AUFRUFER gibt seinen Kontext herein, der Renderer holt sich keinen
  // eigenen. Hier stand `canvas.getContext("2d", { alpha: false })`: die
  // Attribute wirken nur beim ERSTEN Aufruf je Canvas, und weil jeder Aufrufer
  // vorher selbst `getContext("2d")` ruft, blieben sie stumm ohne Wirkung.
  // Hätten sie gewirkt, wäre der Schaden genau das gesuchte Viereck — auf einer
  // Flaeche ohne Alphakanal füllt `clearRect` mit deckendem Schwarz.
  constructor(ctx: CanvasRenderingContext2D) {
    this.ctx = ctx;
    this.buf = document.createElement("canvas");
    this.gbuf = document.createElement("canvas");
    const b = this.buf.getContext("2d");
    const g = this.gbuf.getContext("2d");
    if (!b || !g) throw new Error("Canvas-2D-Kontext nicht verfügbar");
    this.bctx = b;
    this.gctx = g;
  }

  setSize(w: number, h: number, dpr: number): void {
    this.w = w;
    this.h = h;
    this.dpr = dpr;
  }

  private tone(c: Hsl, k: number, a: number): string {
    // Praesenz statt Helligkeit: im dunklen Theme heisst „stark" hell, im hellen
    // „stark" dunkel. Mehrfach multipliziert laeuft jede Tinte sonst nach Schwarz.
    const al = this.pal.isDark ? a : Math.min(1, a * 1.25);
    const l = this.pal.isDark
      ? lerp(Math.max(2, c.l * 0.3), Math.min(97, c.l + 26), clamp(k, 0, 1))
      : lerp(Math.min(96, c.l + 24), Math.max(14, c.l - 16), clamp(k, 0, 1));
    return css({ h: c.h, s: c.s, l }, al);
  }

  private rg(c: CanvasRenderingContext2D, x: number, y: number, r0: number, r1: number,
             stops: [number, string][]): CanvasGradient {
    const g = c.createRadialGradient(x, y, Math.max(0, r0), x, y, Math.max(0.01, r1));
    for (const [p, col] of stops) g.addColorStop(clamp(p, 0, 1), col);
    return g;
  }

  private lgr(c: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number,
              stops: [number, string][]): CanvasGradient {
    const g = c.createLinearGradient(x0, y0, x1, y1);
    for (const [p, col] of stops) g.addColorStop(clamp(p, 0, 1), col);
    return g;
  }

  private blobPath(c: CanvasRenderingContext2D, cx: number, cy: number, R: number,
                   shape: (a: number) => number, seg = 132): void {
    c.beginPath();
    for (let i = 0; i <= seg; i++) {
      const a = (i / seg) * TAU;
      const r = R * shape(a);
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
      if (i) c.lineTo(x, y);
      else c.moveTo(x, y);
    }
    c.closePath();
  }

  /** Glanz: addieren geht nur im Dunkeln, im Hellen wird die Tinte ausgespart. */
  private gleam(x: number, y: number, rx: number, ry: number, rot: number, k: number): void {
    const c = this.ctx;
    const prev = c.globalCompositeOperation;
    c.save();
    c.translate(x, y);
    c.rotate(rot);
    c.scale(1, Math.max(0.002, ry / rx));
    if (this.pal.isDark) {
      c.globalCompositeOperation = "lighter";
      c.fillStyle = this.rg(c, 0, 0, 0, rx, [
        [0, this.tone(this.pal.hi, 0.95, k)],
        [0.55, this.tone(this.pal.hi, 0.9, k * 0.35)],
        [1, this.tone(this.pal.hi, 0.85, 0)],
      ]);
    } else {
      c.globalCompositeOperation = "source-over";
      c.fillStyle = this.rg(c, 0, 0, 0, rx, [
        [0, css(this.pal.card, k * 1.1)],
        [0.6, css(this.pal.card, k * 0.4)],
        [1, css(this.pal.card, 0)],
      ]);
    }
    c.beginPath();
    c.arc(0, 0, rx, 0, TAU);
    c.fill();
    c.restore();
    c.globalCompositeOperation = prev;
  }

  private buffer(fine = false): CanvasRenderingContext2D {
    // Zwischenpuffer gedeckelt: sonst waechst der Weichzeichner-Radius mit der
    // Bildbreite mit, und dessen Kosten steigen ueberproportional.
    //
    // Bis 0.232.0 lag die Obergrenze bei 0.5 — der Koerper wurde also auf HALBER
    // Auflösung gemalt und beim Auftragen 2x hochgezogen. Feine Adern verlieren
    // dabei sichtbar. Gemessen an einem deterministischen Koerper (754x377,
    // „Beleuchtet"): Schaerfe 1,955 -> 2,176 (+11 %) bei +4 % Zeichenzeit; die
    // Adern gewinnen mehr, weil duenne Linien beim Hochziehen am meisten
    // verlieren. Der Deckel bleibt, nur hoeher: grosse Buehnen bleiben bezahlbar.
    // **Das Budget zaehlt GERAETE-Pixel, und der Puffer darf bis zur
    // Geraeteaufloesung gehen.** Bis 0.352.0 stand hier `Math.min(1, …)` und
    // `this.dpr` wurde nirgends gelesen — der Puffer war also HOECHSTENS ein
    // Pixel je CSS-Pixel und auf einem HiDPI-Schirm damit nie besser als die
    // halbe Aufloesung der Flaeche, auf die er gemalt wird.
    //
    // Schon bei dpr 1 traf es fuenf der sechs Koerper: bei 722 px Buehnenkante
    // ergab die alte Grenze 520/722 = 0,72 — der Koerper wurde auf 72 % gemalt
    // und wieder hochgezogen. Im Bild gemessen (2026-08-25, „Beleuchtet"):
    // treppenfoermige Silhouette, waehrend „Adern, durchsichtig" mit `fine`
    // (900/722 = 1,0) an derselben Stelle eine saubere Kante zeigte. Die
    // Trennlinie lief exakt am `fine`-Schalter entlang.
    const maxGeraet = fine ? 1800 : 1600;
    this.bs = Math.min(this.dpr, maxGeraet / Math.max(this.bw, this.bh, 1));
    const bw = Math.max(2, Math.ceil(this.bw * this.bs));
    const bh = Math.max(2, Math.ceil(this.bh * this.bs));
    if (this.buf.width !== bw || this.buf.height !== bh) {
      this.buf.width = bw;
      this.buf.height = bh;
    }
    const b = this.bctx;
    // Verschoben statt bei null: die Maler rechnen weiter in Bühnenkoordinaten
    // (`cx`, `cy`, `fillRect(0,0,w,h)`), der Puffer deckt aber nur den
    // Ausschnitt ab. Ohne die Verschiebung läge der Orb ausserhalb.
    b.setTransform(this.bs, 0, 0, this.bs, -this.bx * this.bs, -this.by * this.bs);
    b.globalCompositeOperation = "source-over";
    b.globalAlpha = 1;
    b.filter = "none";
    b.clearRect(this.bx, this.by, this.bw, this.bh);
    b.globalCompositeOperation = this.pal.add;
    return b;
  }

  private bblur(px: number): void {
    this.bctx.filter = px > 0.05 ? `blur(${(px * this.bs).toFixed(2)}px)` : "none";
  }

  /** Leuchten durch Verkleinern statt durch `filter: blur()`: ueber die volle
   *  Flaeche kostet ein Filter mehr Zeit als das gesamte uebrige Bild. */
  private glow(alpha: number, soft = 0.09): void {
    const gw = Math.max(2, Math.round(this.bw * soft));
    const gh = Math.max(2, Math.round(this.bh * soft));
    if (this.gbuf.width !== gw || this.gbuf.height !== gh) {
      this.gbuf.width = gw;
      this.gbuf.height = gh;
    }
    const g = this.gctx;
    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, gw, gh);
    g.drawImage(this.buf, 0, 0, gw, gh);
    const c = this.ctx;
    c.filter = "none";
    c.globalCompositeOperation = this.pal.add;
    c.globalAlpha = alpha;
    c.drawImage(this.gbuf, this.bx, this.by, this.bw, this.bh);
    c.globalAlpha = 1;
  }

  private paint(alpha = 1): void {
    const c = this.ctx;
    c.globalCompositeOperation = this.pal.add;
    c.globalAlpha = alpha;
    c.drawImage(this.buf, this.bx, this.by, this.bw, this.bh);
    c.globalAlpha = 1;
    c.globalCompositeOperation = "source-over";
  }

  private ensureVeins(): Vein[] {
    if (this.veins) return this.veins;
    const pts: Vein[] = [];
    for (let i = 0; i < 200; i++) {
      const u = Math.random() * 2 - 1, th = Math.random() * TAU, r = Math.sqrt(1 - u * u);
      pts.push({
        x: r * Math.cos(th), y: r * Math.sin(th), z: u, hist: [],
        sp: 0.5 + Math.random() * 0.9, off: Math.random() * 120,
        age: Math.random() * 300, span: 240 + Math.random() * 300,
        wide: 0.5 + Math.random() * 1.1, ph: Math.random() * TAU,
      });
    }
    for (let k = 0; k < 34; k++) {
      for (const q of pts) {
        this.flowStep(q, 0, 1.8, 0.02 * q.sp);
        q.hist.push(q.x, q.y, q.z);
      }
    }
    this.veins = pts;
    return pts;
  }

  /** Tangential zur Kugel stroemen. Jede Ader bekommt ihren eigenen Versatz im
   *  Feld — mit einem gemeinsamen Feld laufen alle in dieselben Senken und die
   *  Kugel steht nach Sekunden leer. */
  private flowStep(q: Vein, t: number, freq: number, step: number): void {
    const o = q.off;
    const a = n3(q.x * freq + 11 + o, q.y * freq + 3 + o * 0.7, q.z * freq + t * 0.12 + o * 0.3) * Math.PI * 3.2;
    let ex = 0, ey = 0;
    if (Math.abs(q.x) < 0.9) ex = 1;
    else ey = 1;
    let ux = q.y * 0 - q.z * ey, uy = q.z * ex - q.x * 0, uz = q.x * ey - q.y * ex;
    const ul = Math.hypot(ux, uy, uz) || 1;
    ux /= ul; uy /= ul; uz /= ul;
    const vx = q.y * uz - q.z * uy, vy = q.z * ux - q.x * uz, vz = q.x * uy - q.y * ux;
    const ca = Math.cos(a), sa = Math.sin(a);
    const nx = q.x + (ca * ux + sa * vx) * step;
    const ny = q.y + (ca * uy + sa * vy) * step;
    const nz = q.z + (ca * uz + sa * vz) * step;
    const m = Math.hypot(nx, ny, nz) || 1;
    q.x = nx / m; q.y = ny / m; q.z = nz / m;
  }

  private litBall(cx: number, cy: number, R: number, shape: (a: number) => number): void {
    const p = this.pal;
    const lx = cx - R * 0.34, ly = cy - R * 0.4;
    const b = this.buffer();
    b.globalCompositeOperation = "source-over";
    b.fillStyle = this.rg(b, lx, ly, R * 0.02, R * 1.5, [
      [0, this.tone(p.ink[4], 0.86, 1)], [0.28, this.tone(p.ink[3], 0.7, 1)],
      [0.6, this.tone(p.prim, 0.5, 1)], [0.86, this.tone(p.prim, 0.3, 1)], [1, this.tone(p.prim, 0.18, 1)],
    ]);
    b.fillRect(0, 0, this.w, this.h);
    b.globalCompositeOperation = "multiply";
    b.fillStyle = this.rg(b, cx + R * 0.5, cy + R * 0.55, R * 0.15, R * 1.3, [
      [0, css(shift(p.bg, p.isDark ? 4 : -4), 0.85)], [0.65, css(p.bg, 0.35)], [1, css(p.bg, 0)],
    ]);
    b.fillRect(0, 0, this.w, this.h);
    b.globalCompositeOperation = "destination-in";
    this.bblur(R * 0.012);
    b.fillStyle = "#fff";
    this.blobPath(b, cx, cy, R, shape);
    b.fill();
    b.filter = "none";
    this.ctx.globalCompositeOperation = "source-over";
    this.ctx.drawImage(this.buf, this.bx, this.by, this.bw, this.bh);
  }

  /** Randlicht folgt der Silhouette — als exakter Kreis uebermalt es jede Beule. */
  private ballRim(cx: number, cy: number, R: number, level: number, shape: (a: number) => number): void {
    const c = this.ctx, p = this.pal;
    c.globalCompositeOperation = p.add;
    c.fillStyle = this.rg(c, cx, cy, R * 0.72, R * 1.02, [
      [0, this.tone(p.hi, 0.9, 0)],
      [0.78, this.tone(p.hi, 0.92, 0.22 * (0.5 + 0.5 * level))],
      [1, this.tone(p.hi, 0.9, 0)],
    ]);
    this.blobPath(c, cx, cy, R * 1.02, shape);
    c.fill();
    this.gleam(cx - R * 0.28, cy - R * 0.36, R * 0.3, R * 0.19, -0.6, 0.4);
    c.globalCompositeOperation = "source-over";
  }

  private veinField(cx: number, cy: number, R: number, st: VoiceOrbState, t: number,
                    solid: boolean, shape: (a: number) => number): void {
    const p = this.pal;
    const dt = this.lastT === null ? 0 : clamp(t - this.lastT, 0, 0.06);
    const step = dt * (0.05 + 0.5 * st.spin) * (0.5 + st.level * 0.8);
    const keep = clamp(Math.round(0.55 / Math.max(step, 0.0006)), 14, 90);
    const b = this.buffer(true);
    // Die Silhouette begrenzt die Adern: der Radiusfaktor wird je Ader nur
    // einmal ausgewertet, bei starker Verformung stuenden sie sonst ueber.
    b.save();
    this.blobPath(b, cx, cy, R * 1.005, shape);
    b.clip();
    for (const q of this.ensureVeins()) {
      this.flowStep(q, t, 1.8, step * q.sp);
      if (++q.age > q.span) {
        q.age = 0;
        const u = Math.random() * 2 - 1, th = Math.random() * TAU, rr = Math.sqrt(1 - u * u);
        q.x = rr * Math.cos(th); q.y = rr * Math.sin(th); q.z = u;
        q.hist.length = 0;
      }
      // Erst ausblenden, dann versetzen: bei voller Deckung verschwand die Ader
      // an einer Stelle und blitzte anderswo wieder auf.
      const lifeU = q.age / q.span;
      const lifeFade = smoothstep(0, 0.14, lifeU) * smoothstep(1, 0.82, lifeU);
      if (lifeFade < 0.01) continue;
      q.hist.push(q.x, q.y, q.z);
      while (q.hist.length > keep * 3) q.hist.splice(0, 3);
      const n = q.hist.length / 3;
      if (n < 3) continue;
      const dep = clamp(q.z * 0.5 + 0.5, 0, 1);
      const face = solid ? smoothstep(-0.12, 0.22, q.z) : 1;
      if (face < 0.01) continue;
      const flare = 0.35 + 0.65 * Math.pow(0.5 + 0.5 * Math.sin(t * 0.9 + q.ph), 2.2);
      const lit = solid ? clamp(0.5 + 0.5 * (-q.x * 0.6 - q.y * 0.7 + q.z * 0.4), 0, 1) : 1;
      const Rq = R * shape(Math.atan2(q.y, q.x));
      b.beginPath();
      b.moveTo(cx + q.hist[0] * Rq, cy + q.hist[1] * Rq);
      for (let j = 1; j < n; j++) b.lineTo(cx + q.hist[j * 3] * Rq, cy + q.hist[j * 3 + 1] * Rq);
      const col = solid ? p.hi : p.prim;
      const kk = solid ? (p.isDark ? 0.92 : 0.55) : (p.isDark ? 0.62 : 0.42) + 0.32 * dep;
      const aa = (solid ? (0.07 + 0.85 * dep * dep) * (0.3 + lit) : (0.09 + 0.62 * dep * dep) * flare)
        * face * lifeFade * (0.5 + st.level * 0.65);
      // Verlauf entlang der Spur: mit einer Farbe endet sie abgehackt.
      b.strokeStyle = this.lgr(b, cx + q.hist[0] * Rq, cy + q.hist[1] * Rq,
        cx + q.hist[(n - 1) * 3] * Rq, cy + q.hist[(n - 1) * 3 + 1] * Rq,
        [[0, this.tone(col, kk, 0)], [0.3, this.tone(col, kk, aa * 0.45)], [1, this.tone(col, kk, aa)]]);
      b.lineWidth = (0.5 + 1.5 * dep) * q.wide;
      b.stroke();
    }
    b.restore();
    this.glow(0.45, 0.11);
    this.paint(solid ? 0.95 : 1);
  }

  private irisBody(cx: number, cy: number, R: number, st: VoiceOrbState, t: number,
                   shape: (a: number) => number): void {
    const p = this.pal, c = this.ctx;
    const b = this.buffer();
    b.globalCompositeOperation = "source-over";
    b.fillStyle = this.rg(b, cx - R * 0.3, cy - R * 0.35, 0, R * 1.4, [
      [0, this.tone(p.ink[3], 0.72, 1)], [0.4, this.tone(p.prim, 0.6, 1)],
      [0.75, this.tone(p.ink[1], 0.42, 1)], [1, this.tone(p.ink[0], 0.26, 1)],
    ]);
    b.fillRect(0, 0, this.w, this.h);
    b.globalCompositeOperation = p.add;
    const fl = 0.3 * (0.35 + 1.3 * st.spin);
    for (let i = 0; i < 5; i++) {
      const a = t * fl * (i % 2 ? 1 : -1) + i * 1.7;
      const dist = R * (0.34 - 0.16 * st.focus) * (0.7 + 1.1 * n3(t * 0.4 + i * 3.3, i, 0));
      b.fillStyle = this.rg(b, cx + Math.cos(a) * dist, cy + Math.sin(a * 1.2 + i) * dist, 0,
        R * (0.5 + 0.25 * n3(t * 0.5 + i, 0, i)),
        [[0, this.tone(p.ink[i], 0.62, 0.55)], [1, this.tone(p.ink[i], 0.5, 0)]]);
      b.fillRect(0, 0, this.w, this.h);
    }
    b.globalCompositeOperation = "destination-in";
    this.bblur(R * 0.012);
    b.fillStyle = "#fff";
    this.blobPath(b, cx, cy, R, shape);
    b.fill();
    b.filter = "none";
    c.globalCompositeOperation = "source-over";
    c.drawImage(this.buf, this.bx, this.by, this.bw, this.bh);
    // Der Saum macht die Blase zur Blase. Als Bogen endete er mit einer
    // sichtbaren Kappe — als geschlossener Kreis blendet er an beiden Enden aus.
    c.globalCompositeOperation = p.add;
    const irisA = 0.45 * (0.6 + 0.5 * st.level);
    for (const [lwF, aF] of [[0.085, 0.3], [0.05, 0.55], [0.022, 1]]) {
      c.lineWidth = R * lwF;
      const a = irisA * aF;
      if (typeof c.createConicGradient === "function") {
        const g = c.createConicGradient(Math.PI * 0.2, cx, cy);
        g.addColorStop(0, this.tone(p.ink[0], 0.8, 0));
        g.addColorStop(0.08, this.tone(p.ink[0], 0.8, a * 0.45));
        g.addColorStop(0.2, this.tone(p.ink[1], 0.85, a));
        g.addColorStop(0.33, this.tone(p.ink[2], 0.88, a * 0.95));
        g.addColorStop(0.45, this.tone(p.ink[4], 0.9, a * 0.78));
        g.addColorStop(0.55, this.tone(p.ink[3], 0.82, a * 0.38));
        g.addColorStop(0.64, this.tone(p.ink[3], 0.78, 0));
        g.addColorStop(1, this.tone(p.ink[3], 0.78, 0));
        c.strokeStyle = g;
      } else {
        c.strokeStyle = this.tone(p.ink[2], 0.85, a * 0.6);
      }
      this.blobPath(c, cx, cy, R * 0.985, shape);
      c.stroke();
    }
    this.gleam(cx - R * 0.4, cy - R * 0.55, R * 0.32, R * 0.13, -0.7, 0.45);
    c.globalCompositeOperation = "source-over";
  }

  private glassBody(cx: number, cy: number, R: number, st: VoiceOrbState, t: number,
                    shape: (a: number) => number): void {
    const p = this.pal, c = this.ctx;
    const b = this.buffer();
    this.bblur(R * 0.045);
    for (const L of [
      { i: 0, f: 1.15, sp: 1.0, ph: 0, y: -0.1 },
      { i: 2, f: 1.7, sp: -0.8, ph: 2.1, y: 0.14 },
      { i: 4, f: 0.85, sp: 1.35, ph: 4.0, y: 0.02 },
    ]) {
      const col = p.ink[L.i];
      const A = R * 0.35 * (0.2 + st.level * (0.7 + 0.5 * st.pulse));
      const fq = L.f * (0.75 + 1.1 * st.focus);
      const bell = (u: number): number => Math.pow(Math.max(0, 1 - u * u), 0.7);
      const yy = (u: number): number =>
        cy + L.y * R * (1 - 0.5 * st.focus)
        + Math.sin(u * TAU * fq + t * L.sp * (0.4 + 1.4 * st.spin) + L.ph) * A * bell(u)
        + n3(u * 2 + t * 0.7 + L.ph, 0, 0) * A * 0.32 * bell(u);
      const th = (u: number): number => R * 0.085 * bell(u) * (0.45 + st.level * 0.8);
      b.beginPath();
      let first = true;
      for (let x = cx - R; x <= cx + R; x += 4) {
        const u = (x - cx) / R;
        if (first) { b.moveTo(x, yy(u) - th(u)); first = false; } else b.lineTo(x, yy(u) - th(u));
      }
      for (let x = cx + R; x >= cx - R; x -= 4) {
        const u = (x - cx) / R;
        b.lineTo(x, yy(u) + th(u));
      }
      b.closePath();
      b.fillStyle = this.lgr(b, cx - R, 0, cx + R, 0, [
        [0, this.tone(col, 0.6, 0.02)], [0.3, this.tone(col, 0.68, 0.85)],
        [0.55, this.tone(p.hi, 0.92, 0.5)], [0.8, this.tone(col, 0.68, 0.68)], [1, this.tone(col, 0.6, 0.02)],
      ]);
      b.fill();
    }
    b.globalCompositeOperation = "destination-in";
    this.bblur(R * 0.03);
    b.fillStyle = "#fff";
    this.blobPath(b, cx, cy, R * 0.97, shape);
    b.fill();
    b.filter = "none";
    this.glow(0.35, 0.09);
    this.paint(1);
    c.globalCompositeOperation = "source-over";
    c.lineWidth = R * 0.02;
    c.strokeStyle = this.lgr(c, cx - R, cy - R, cx + R, cy + R, [
      [0, this.tone(p.hi, 0.85, 0.55)], [0.45, this.tone(p.prim, 0.6, 0.22)], [1, this.tone(p.hi, 0.8, 0.4)],
    ]);
    this.blobPath(c, cx, cy, R, shape);
    c.stroke();
    this.gleam(cx - R * 0.44, cy - R * 0.55, R * 0.26, R * 0.13, -0.7, 0.3);
  }

  private hollowBody(cx: number, cy: number, R: number, st: VoiceOrbState, t: number,
                     shape: (a: number) => number): void {
    const p = this.pal, c = this.ctx;
    const b = this.buffer();
    const SEG = 120;
    for (let i = 0; i < 5; i++) {
      const k = 2 + i;
      const sp = (0.1 + 1.1 * st.spin) * (i % 2 ? -1 : 1) * (1 + 0.3 * i);
      const wob = 0.078 * (0.4 + 0.9 * st.level) * (1 - 0.45 * st.focus);
      const kw = Math.max(1, Math.round(k * 0.8));
      const rr = (a: number): number =>
        R * shape(a) * (1 + wob * Math.sin(k * a + t * sp + i * 2.1) + wob * 0.5 * angleNoise(a, 1.4, t * 0.4 + i * 5));
      const ww = (a: number): number =>
        R * 0.08 * (0.18 + 0.95 * (0.5 + 0.5 * Math.sin(kw * a - t * sp * 1.3 + i * 3.7))) * (0.55 + 0.6 * st.level);
      b.beginPath();
      for (let j = 0; j <= SEG; j++) {
        const a = (j / SEG) * TAU, r = rr(a) + ww(a) * 0.5;
        const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
        if (j) b.lineTo(x, y); else b.moveTo(x, y);
      }
      for (let j = SEG; j >= 0; j--) {
        const a = (j / SEG) * TAU, r = rr(a) - ww(a) * 0.5;
        b.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      }
      b.closePath();
      const gl = 0.95 * (0.5 + 0.5 * st.level);
      if (typeof b.createConicGradient === "function") {
        const g = b.createConicGradient(t * (0.1 + 0.6 * st.spin) * (i % 2 ? -1 : 1) + i * 1.3, cx, cy);
        g.addColorStop(0, this.tone(p.prim, 0.4, 0.1 * gl));
        g.addColorStop(0.22, this.tone(p.prim, 0.72, 0.42 * gl));
        g.addColorStop(0.44, this.tone(p.hi, 0.95, 0.7 * gl));
        g.addColorStop(0.66, this.tone(p.prim, 0.6, 0.3 * gl));
        g.addColorStop(1, this.tone(p.prim, 0.4, 0.1 * gl));
        b.fillStyle = g;
      } else {
        b.fillStyle = this.tone(p.prim, 0.7, 0.38 * gl);
      }
      b.fill();
    }
    this.glow(0.6, 0.1);
    this.paint(0.92);
    c.globalCompositeOperation = "source-over";
    c.fillStyle = this.rg(c, cx, cy, 0, R * 0.8, [
      [0, css(p.card, 0.85)], [0.65, css(p.card, 0.4)], [1, css(p.card, 0)],
    ]);
    c.beginPath();
    c.arc(cx, cy, R * 0.8, 0, TAU);
    c.fill();
  }

  /**
   * Letzter Schritt jedes Bildes: zum Rand hin ausblenden.
   *
   * Die Deckelung der Aura allein reicht nicht — Ringe (bis R*2.26) und Funken
   * fliegen absichtlich nach aussen, und wo sie die Kante treffen, schneidet
   * das Rechteck sie hart ab. Gemessen 2026-08-18 mit allen Animationen: bis
   * Alpha 78 von 255 auf der Randlinie. Statt jeden Effekt einzeln zu
   * beschneiden — was ihre Form zerstoert — blendet der Rand selbst aus.
   *
   * Vier lineare Verlaeufe statt eines radialen: der Abstand, der zaehlt, ist
   * der zur naechsten KANTE. Ein Kreis laesst die Ecken stehen, und genau dort
   * bliebe der Schnitt sichtbar.
   */
  private randAusblenden(): void {
    const c = this.ctx;
    const band = Math.max(6, Math.min(this.w, this.h) * 0.07);
    const weg = "rgba(0,0,0,1)", bleibt = "rgba(0,0,0,0)";
    c.globalCompositeOperation = "destination-out";
    c.globalAlpha = 1;
    c.filter = "none";
    // Nur den Saum fuellen, nicht viermal die ganze Flaeche. Jenseits des
    // Bandes ist die Deckkraft des Verlaufs ohnehin 0 — das Ergebnis ist
    // identisch, die Fuellflaeche aber rund achtzehnmal kleiner (1200x675:
    // 176k statt 3,24M Pixel je Bild).
    const seiten: [number, number, number, number, number, number, number, number][] = [
      [0, 0, band, 0, 0, 0, band, this.h],                            // links
      [this.w, 0, this.w - band, 0, this.w - band, 0, band, this.h],  // rechts
      [0, 0, 0, band, 0, 0, this.w, band],                            // oben
      [0, this.h, 0, this.h - band, 0, this.h - band, this.w, band],  // unten
    ];
    for (const [x0, y0, x1, y1, rx, ry, rw, rh] of seiten) {
      c.fillStyle = this.lgr(c, x0, y0, x1, y1, [[0, weg], [1, bleibt]]);
      c.fillRect(rx, ry, rw, rh);
    }
    c.globalCompositeOperation = "source-over";
  }

  /**
   * Der Schein — als ELLIPSE, nicht als Kreis.
   *
   * Er will bis `R*(2+0.9*level)` reichen, also `0.70*min(w,h)`, verfuegbar
   * sind `0.5*min(w,h)`. Auf einen Kreis gedeckelt (so stand es bis 0.231.0)
   * bindet ihn die KUERZERE Seite: im Testanruf-Format 260x180 endete er bei
   * 90 px, waehrend links und rechts je 130 px ungenutzt blieben — die Figur
   * wirkt in ihre Box gezwaengt. Ein Radialverlauf ist immer kreisfoermig,
   * deshalb wird die Flaeche skaliert statt der Radius gedeckelt: je Achse so
   * weit, wie dort Platz ist. Der Koerper behaelt seine Groesse.
   */
  private auraFx(cx: number, cy: number, R: number, level: number): void {
    const c = this.ctx, p = this.pal;
    const wunsch = R * (2 + 0.9 * level);
    const rx = Math.max(1, Math.min(wunsch, cx, this.w - cx));
    const ry = Math.max(1, Math.min(wunsch, cy, this.h - cy));
    const basis = Math.min(rx, ry);
    c.save();
    c.translate(cx, cy);
    c.scale(rx / basis, ry / basis);
    c.globalCompositeOperation = p.add;
    c.fillStyle = this.rg(c, 0, 0, Math.min(R * 0.7, basis * 0.7), basis, [
      [0, this.tone(p.prim, 0.34, 0.3 + 0.24 * level)],
      [0.5, this.tone(p.prim, 0.26, 0.14)],
      [1, this.tone(p.prim, 0.2, 0)],
    ]);
    // Im skalierten Raum: grosszuegig, damit die Flaeche sicher gedeckt ist.
    c.fillRect(-this.w, -this.h, this.w * 2, this.h * 2);
    c.restore();
    c.globalCompositeOperation = "source-over";
  }

  private orbitFx(cx: number, cy: number, R: number, st: VoiceOrbState, t: number,
                  bands: number, front: boolean): void {
    const p = this.pal;
    const b = this.buffer();
    for (let i = 0; i < bands; i++) {
      const rot = t * (0.05 + 0.75 * st.spin) * (i % 2 ? -1 : 1) + i * 1.9;
      const rx = R * (1.3 - 0.16 * st.focus + 0.07 * i);
      const ry = rx * Math.abs(Math.sin(t * (0.12 + 0.42 * st.spin) + i * 1.3)) * 0.7 + R * 0.24;
      const a0 = front ? Math.PI : 0, a1 = front ? TAU : Math.PI;
      // Schmaler als bis 0.232.0 (dort 0.55*R): der breiteste Durchgang war
      // fast so dick wie der halbe Koerper und legte sich als Milchschleier
      // ueber alles. Der Saum traegt die Themenfarbe, nur der Kern ist hell.
      const base = R * 0.34;
      const col = i % 2 ? mixC(p.hi, p.prim, 0.55) : mixC(p.hi, p.prim, 0.25);
      for (let j = 0; j < 4; j++) {
        const fr = 1 - j / 4;
        b.strokeStyle = this.tone(j === 3 ? p.hi : col, p.isDark ? 0.88 : 0.5,
          (0.03 + 0.62 * Math.pow(1 - fr, 2.8)) * (p.isDark ? 1 : 0.42) * (0.5 + 0.75 * st.level));
        b.lineWidth = base * Math.pow(fr, 1.5) + 0.8;
        b.beginPath();
        b.ellipse(cx, cy, rx, ry, rot, a0, a1);
        b.stroke();
      }
    }
    // EIN Leuchten statt zwei. Der erste Durchgang lief mit soft=0.06, also
    // einem Puffer von 6 % der Bildbreite, der um das Siebzehnfache wieder
    // hochgezogen wurde — das ist der Treppenlook, den man als „schlechte
    // Aufloesung" sieht. Die Untergrenze 0.16 haelt die Hochskalierung unter 7x.
    this.glow(0.62, clamp(200 / Math.max(this.w, this.h), 0.16, 0.5));
    this.ctx.globalCompositeOperation = "source-over";
  }

  private streamFx(cx: number, cy: number, R: number, st: VoiceOrbState, t: number, bands: number): void {
    const c = this.ctx, p = this.pal;
    if (!this.streamers) {
      const a: Streamer[] = [];
      for (let i = 0; i < 230; i++) {
        a.push({ u: Math.random(), lane: i % 4, sp: 0.7 + Math.random() * 0.7, sz: 0.5 + Math.random() * 1.3 });
      }
      this.streamers = a;
    }
    c.globalCompositeOperation = p.add;
    for (const q of this.streamers) {
      if (q.lane >= bands) continue;
      const i = q.lane;
      // Kreisbahn in einer geneigten Ebene: erst neigen, dann in der Bildebene
      // drehen. Die Tiefe entscheidet ueber Groesse und Deckkraft.
      const tilt = 0.45 + 0.5 * Math.sin(t * 0.17 + i * 1.7);
      const rot = t * (0.1 + 0.55 * st.spin) * (i % 2 ? -1 : 1) + i * 2.1;
      const phi = ((q.u + t * (0.03 + 0.22 * st.spin) * q.sp) % 1) * TAU;
      const rb = R * (1.18 + 0.1 * i);
      const px = Math.cos(phi), py = Math.sin(phi) * Math.cos(tilt), pz = Math.sin(phi) * Math.sin(tilt);
      const X = px * Math.cos(rot) - py * Math.sin(rot);
      const Y = px * Math.sin(rot) + py * Math.cos(rot);
      // Hinter der Kugel verschwinden die Teilchen, statt darueber zu laufen.
      if (pz < 0 && Math.hypot(X, Y) * rb < R) continue;
      const x = cx + X * rb, y = cy + Y * rb;
      const dep = clamp(pz * 0.5 + 0.5, 0, 1);
      const rr = Math.max(0.8, q.sz * (0.5 + 1.5 * dep) * (R * 0.012) * (0.6 + 0.7 * st.level) * 3.2);
      const al = (0.12 + 0.75 * dep) * (0.35 + 0.75 * st.level);
      c.fillStyle = this.rg(c, x, y, 0, rr, [
        [0, this.tone(p.hi, 0.95, al)],
        [0.35, this.tone(mixC(p.hi, p.prim, 0.5), 0.85, al * 0.4)],
        [1, this.tone(p.prim, 0.7, 0)],
      ]);
      c.beginPath();
      c.arc(x, y, rr, 0, TAU);
      c.fill();
    }
    c.globalCompositeOperation = "source-over";
  }

  private ringsFx(cx: number, cy: number, R: number, st: VoiceOrbState, t: number): void {
    const c = this.ctx, p = this.pal;
    // Der Silbenstoss gilt nur ein Bild lang; ohne Zeitstempel-Vergleich geht er
    // verloren, sobald eine Buehne ein Bild aussetzt.
    if (st.lastSyllable > this.seenSyllRing) {
      this.seenSyllRing = st.lastSyllable;
      this.rings.push({ born: t, power: clamp(0.5 + st.level, 0, 1.6) });
    }
    while (this.rings.length > 8) this.rings.shift();
    c.globalCompositeOperation = p.add;
    for (const r of this.rings) {
      const age = (t - r.born) / 1.15;
      if (age >= 1) continue;
      const rad = R * (0.78 + 1.45 * Math.pow(age, 0.62));
      const a = Math.pow(1 - age, 2.1) * 0.6 * r.power;
      if (a < 0.004) continue;
      // Schmal und mit hellem Kern statt breit und weich: bei der alten Breite
      // (0.17*R Halbbreite) lag die gemessene Halbwertsbreite bei 45 px auf
      // 169 px Koerperradius — das liest sich als Schmier, nicht als Welle.
      // Die Flanken tragen die Themenfarbe, nur der Kern ist fast weiss; mit
      // `hi` ueber die ganze Breite wirkte der Ring grau (dessen Saettigung ist
      // auf 34 gedeckelt).
      const wd = R * (0.018 + 0.07 * (1 - age));
      c.fillStyle = this.rg(c, cx, cy, Math.max(0, rad - wd), rad + wd, [
        [0, this.tone(p.prim, 0.6, 0)],
        [0.3, this.tone(p.prim, 0.78, a * 0.3)],
        [0.5, this.tone(p.hi, 0.98, a * 1.3)],
        [0.7, this.tone(p.prim, 0.78, a * 0.3)],
        [1, this.tone(p.prim, 0.6, 0)],
      ]);
      // Nur das Band fuellen, nicht die ganze Scheibe.
      c.beginPath();
      c.arc(cx, cy, rad + wd, 0, TAU);
      c.arc(cx, cy, Math.max(0, rad - wd), 0, TAU, true);
      c.fill();
    }
    c.globalCompositeOperation = "source-over";
  }

  private sparksFx(cx: number, cy: number, R: number, st: VoiceOrbState, t: number): void {
    const c = this.ctx, p = this.pal;
    if (st.lastSyllable > this.seenSyllSpark) {
      this.seenSyllSpark = st.lastSyllable;
      const n = 5 + Math.round(9 * st.level);
      for (let i = 0; i < n; i++) {
        this.sparks.push({ born: t, a: Math.random() * TAU, sp: 0.6 + Math.random() * 1.1, size: 0.6 + Math.random() * 1.8 });
      }
    }
    while (this.sparks.length > 160) this.sparks.shift();
    c.globalCompositeOperation = p.add;
    for (const sp of this.sparks) {
      const age = t - sp.born;
      if (age >= 1) continue;
      const dist = R * (0.9 + 1.3 * Math.pow(age, 0.55) * sp.sp);
      const a = Math.pow(1 - age, 2.4) * 0.85;
      const x = cx + Math.cos(sp.a) * dist, y = cy + Math.sin(sp.a) * dist;
      const r = Math.max(0.6, sp.size * (1 - age * 0.55) * (R * 0.012) * 3);
      c.fillStyle = this.rg(c, x, y, 0, r, [
        [0, this.tone(p.hi, 0.95, a)], [0.4, this.tone(p.hi, 0.9, a * 0.35)], [1, this.tone(p.hi, 0.85, 0)],
      ]);
      c.beginPath();
      c.arc(x, y, r, 0, TAU);
      c.fill();
    }
    c.globalCompositeOperation = "source-over";
  }

  /** Zeichnet ein Bild. `t` in Sekunden, monoton steigend. */
  render(cfg: VoiceOrbConfig, st: VoiceOrbState, pal: OrbPalette, t: number): void {
    if (!this.w || !this.h) return;
    this.pal = pal;
    const c = this.ctx;
    c.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    c.globalCompositeOperation = "source-over";
    c.globalAlpha = 1;
    c.filter = "none";
    c.lineCap = "round";
    c.lineJoin = "round";

    const on = (fx: VoiceOrbEffect): boolean => cfg.fx.includes(fx);
    const cx = this.w / 2, cy = this.h / 2;
    let R = Math.min(this.w, this.h) * (0.17 + 0.17 * clamp(cfg.size, 0, 1));
    if (on("breath")) R *= 1 + 0.07 * st.breath * Math.sin(t * 0.8) + st.level * 0.13;

    let shape: (a: number) => number = () => 1;
    if (on("wobble")) {
      const amp = 0.072 * (0.3 + st.level * (0.6 + 0.8 * st.pulse));
      shape = (a: number): number =>
        1 + amp * Math.sin(3 * a + t * (0.6 + st.spin))
        + amp * 0.7 * Math.sin(5 * a - t * (0.9 + st.spin * 0.8))
        + amp * 0.5 * angleNoise(a, 1.7, t * 0.6);
    }
    const bands = clamp(Math.round(cfg.bands), 1, 4);

    // ── Der Ausschnitt des Zwischenpuffers ────────────────────────────────
    //
    // 1,9 R ist gemessen, nicht gegriffen: am weitesten reicht `orbitFx` mit
    // `rx = R * (1.3 - 0.16*focus + 0.07*i)` und einer Strichbreite von
    // `R * 0.34` — bei vier Bändern also bis R * 1,68. Danach kommt `irisBody`
    // mit R * 1,4. Der Rest bleibt unter R * 1,1. Wer hier einen Effekt
    // ergänzt, der weiter hinausreicht, hebt diesen Faktor mit an — sonst
    // schneidet der Puffer ihn ab.
    //
    // Auf die Bühne geklemmt: was ausserhalb liegt, wäre ohnehin unsichtbar,
    // und ein Puffer grösser als die Fläche kostet nur.
    // Der Rand haengt an dem, was tatsaechlich gezeichnet wird. Ohne Baender
    // reicht der Orb kaum ueber R * 1,1 hinaus — den Puffer trotzdem auf
    // R * 1,9 aufzuspannen kostet die dreifache Flaeche fuer nichts.
    const rand = R * (on("orbit") ? 1.9 : cfg.body === "iris" ? 1.5 : 1.2);
    const x0 = Math.max(0, Math.floor(cx - rand));
    const y0 = Math.max(0, Math.floor(cy - rand));
    this.bx = x0;
    this.by = y0;
    this.bw = Math.max(2, Math.min(this.w, Math.ceil(cx + rand)) - x0);
    this.bh = Math.max(2, Math.min(this.h, Math.ceil(cy + rand)) - y0);

    if (on("aura")) this.auraFx(cx, cy, R, st.level);
    if (on("orbit")) this.orbitFx(cx, cy, R, st, t, bands, false);

    switch (cfg.body) {
      case "veinsSolid":
        this.litBall(cx, cy, R, shape);
        this.veinField(cx, cy, R, st, t, true, shape);
        this.ballRim(cx, cy, R, st.level, shape);
        break;
      case "veins":
        c.globalCompositeOperation = pal.add;
        c.fillStyle = this.rg(c, cx - R * 0.2, cy - R * 0.25, R * 0.05, R * 1.05, [
          [0, this.tone(pal.prim, 0.36, 0.52)], [0.55, this.tone(pal.prim, 0.27, 0.34)],
          [1, this.tone(pal.prim, 0.2, 0.12)],
        ]);
        this.blobPath(c, cx, cy, R, shape);
        c.fill();
        c.globalCompositeOperation = "source-over";
        this.veinField(cx, cy, R, st, t, false, shape);
        break;
      case "iris":
        this.irisBody(cx, cy, R, st, t, shape);
        break;
      case "glass":
        this.glassBody(cx, cy, R, st, t, shape);
        break;
      case "hollow":
        this.hollowBody(cx, cy, R, st, t, shape);
        break;
      default:
        this.litBall(cx, cy, R, shape);
        this.ballRim(cx, cy, R, st.level, shape);
    }

    if (on("orbit")) this.orbitFx(cx, cy, R, st, t, bands, true);
    if (on("stream")) this.streamFx(cx, cy, R, st, t, bands);
    if (on("rings")) this.ringsFx(cx, cy, R, st, t);
    if (on("sparks")) this.sparksFx(cx, cy, R, st, t);

    // Muss der LETZTE Schritt sein: alles davor darf ueber den Rand malen,
    // nichts danach. Ein Effekt, der hier angehaengt wird, bekommt sonst
    // wieder die harte Kante.
    this.randAusblenden();

    this.lastT = t;
  }
}

const BODY_IDS = new Set<string>(VOICE_ORB_BODIES.map((b) => b.id));
const FX_IDS = new Set<string>(VOICE_ORB_EFFECTS.map((f) => f.id));

/**
 * Liest eine gespeicherte Einstellung. Cookie und localStorage sind vom
 * Nutzer beschreibbar — ein ungeprueftes `JSON.parse` wuerde jeden Tippfehler
 * und jede alte Fassung als gueltige Konfiguration durchreichen.
 */
export function leseVoiceOrb(roh: string | null | undefined): VoiceOrbConfig | null {
  if (!roh) return null;
  let daten: unknown;
  try {
    daten = JSON.parse(roh);
  } catch {
    return null;
  }
  if (typeof daten !== "object" || daten === null) return null;
  const d = daten as Record<string, unknown>;
  if (typeof d.body !== "string" || !BODY_IDS.has(d.body)) return null;
  const fx = Array.isArray(d.fx)
    ? d.fx.filter((f): f is VoiceOrbEffect => typeof f === "string" && FX_IDS.has(f))
    : [];
  const figur = typeof d.figur === "string" && FIGUR_IDS.has(d.figur) ? (d.figur as FigurVariante) : undefined;
  const farben = typeof d.farben === "string" && FARBEN_IDS.has(d.farben) ? (d.farben as FigurFarben) : undefined;
  const wahl = <T extends string>(wert: unknown, ids: Set<string>): T | undefined =>
    typeof wert === "string" && ids.has(wert) ? (wert as T) : undefined;
  const licht = wahl<AnrufLicht>(d.licht, LICHT_IDS);
  const ansicht = wahl<AnrufAnsicht>(d.ansicht, ANSICHT_IDS);
  const figurArt = wahl<AnrufFigurArt>(d.figurArt, FIGUR_ART_IDS);
  const vollbildText = wahl<AnrufVollbildText>(d.vollbildText, VOLLBILD_TEXT_IDS);
  return {
    body: d.body as VoiceOrbBody,
    size: typeof d.size === "number" && Number.isFinite(d.size) ? clamp(d.size, 0, 1) : VOICE_ORB_DEFAULT.size,
    bands: typeof d.bands === "number" && Number.isFinite(d.bands) ? clamp(Math.round(d.bands), 1, 4) : VOICE_ORB_DEFAULT.bands,
    fx,
    ...(figur ? { figur } : {}),
    ...(farben ? { farben } : {}),
    ...(licht ? { licht } : {}),
    ...(ansicht ? { ansicht } : {}),
    ...(figurArt ? { figurArt } : {}),
    ...(typeof d.schwarm === "boolean" ? { schwarm: d.schwarm } : {}),
    ...(vollbildText ? { vollbildText } : {}),
  };
}
